import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";

/**
 * The Auth0 role that authorizes a user for the staff-only `/admin` surface.
 * Mirrors the server-side `STAFF_ROLE` (api `src/auth/staff.decorator.ts`).
 * DISTINCT from the firm-scoped `"Admin"` role — a firm admin is NOT staff.
 */
export const STAFF_ROLE = "Advice Cloud Staff";

/**
 * Resolution states for the client staff-authorization check. `loading` is
 * the initial, pre-resolution state; `error` is the fail-closed terminal
 * state used on token-resolution failure or timeout
 * (s-admin-authz-shell-advice-019e9cbb R8).
 */
export type StaffAuthorizationStatus =
  "loading" | "staff" | "not-staff" | "error";

/**
 * Fail-closed timeout (ms) for resolving the access token. If Auth0 hasn't
 * resolved within this window the gate treats the user as non-staff rather
 * than hang in the loading state (R8 / AC8).
 */
export const STAFF_AUTH_TIMEOUT_MS = 10000;

interface StaffRolesClaim {
  user_roles?: string[];
}

/**
 * Resolves whether the current user holds the `Advice Cloud Staff` role.
 *
 * Returns an explicit status so the `/admin` gate can:
 *   - show a loading indicator until resolution (`loading`),
 *   - render the shell for staff (`staff`),
 *   - redirect non-staff (`not-staff`),
 *   - FAIL CLOSED on token error or timeout (`error`) — redirect, never hang.
 */
export function useStaffAuthorization(): StaffAuthorizationStatus {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [status, setStatus] = useState<StaffAuthorizationStatus>("loading");

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      setStatus("not-staff");
      return;
    }

    let settled = false;
    const settle = (next: StaffAuthorizationStatus) => {
      if (!settled) {
        settled = true;
        setStatus(next);
      }
    };

    // Fail closed if the token never resolves.
    const timeout = setTimeout(() => settle("error"), STAFF_AUTH_TIMEOUT_MS);

    getAccessTokenSilently()
      .then((token) => {
        const roles = jwtDecode<StaffRolesClaim>(token).user_roles ?? [];
        settle(roles.includes(STAFF_ROLE) ? "staff" : "not-staff");
      })
      .catch(() => {
        // Fail closed on any token-resolution error.
        settle("error");
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      settled = true;
      clearTimeout(timeout);
    };
  }, [getAccessTokenSilently, isAuthenticated, isLoading]);

  return status;
}
